import _ from 'lodash';
import { Currency } from '../bank-account/bank-account';
import { ResponseTranslation } from '../Translations';
import { EmployeeGlAccountKey } from '../employee-account/employee-account';

/** Mirrors Finance.Models PettyCashFundStatus: Draft → ReadyForFunding → Active → Closed (values are stored, not ordered). */
export enum PettyCashFundStatus {
  Draft = 1,
  Active = 2,
  Closed = 3,
  ReadyForFunding = 4,
}

export interface PettyCashFundUser {
  username: string;
  displayName: string;
}

export interface PettyCashFundStatusHistoryEntry {
  date: string;
  status: PettyCashFundStatus;
  user: { username: string | null; firstName: string | null; lastName: string | null } | null;
}

/** A petty cash fund (#5785) — mirrors Finance.Models PettyCashFundDto. */
export interface PettyCashFund {
  id: string;
  /** e.g. NT-FUND-2026-000001 */
  number: string;
  name: string;
  description: string | null;
  financialCompanyReference: string;
  currency: Currency;
  custodian: PettyCashFundUser;
  members: PettyCashFundUser[];
  minRevolvingBalance: number;
  maxRevolvingBalance: number;
  glAccount: EmployeeGlAccountKey;
  claimPolicyReference: string | null;
  claimPolicyName: ResponseTranslation | null;
  /** Sum of posted fund movements; never edited directly. */
  authorizedAmount: number;
  status: PettyCashFundStatus;
  statusHistory: PettyCashFundStatusHistoryEntry[] | null;
  createdBy: string | null;
  createdDate: string | null;
}

/** What the fund page sends on create and update. */
export interface PettyCashFundUpdate {
  name: string;
  description: string | null;
  currency: Currency;
  custodianUsername: string;
  memberUsernames: string[];
  minRevolvingBalance: number;
  maxRevolvingBalance: number;
  glAccountReference: string;
  claimPolicyReference: string | null;
}

export interface PettyCashFundSearchCommand {
  search?: string | null;
  statuses?: PettyCashFundStatus[] | null;
  currency?: Currency | null;
}

export interface PettyCashFundSummary {
  activeFunds: number;
  /** Waiting for the treasurer's opening movement (#5786). */
  readyForFundingFunds: number;
  totalFunds: number;
  authorizedByCurrency: { currency: Currency; amount: number }[];
}

/** The fund's current revolving balance: posted movements plus replenishments less posted expense claims. */
export interface PettyCashFundBalance {
  currency: Currency;
  authorizedAmount: number;
  minRevolvingBalance: number;
  maxRevolvingBalance: number;
  movementsTotal: number;
  /** Posted expense settlements (replenishments, #5799). */
  replenishedTotal: number;
  postedClaimsTotal: number;
  postedClaims: number;
  currentBalance: number;
}

export interface UserOption {
  username: string;
  displayName: string;
}

/** The parts of a UserMaster approval policy the fund page shows. */
export interface ClaimPolicyOption {
  id: string;
  name: ResponseTranslation;
  isActive: boolean;
  minimumApprovals: number;
  approvers: { type: number; value: string; order: number | null; isRequired: boolean; fromAmountUsd: number | null }[];
}

/**
 * The record's own policy as a one-item option list, for users who may not read UserMaster's policy list: the select
 * still shows its name. Approvers are not known without that list, so none are shown.
 */
export function policyOptionOf(policy: { reference: string; name: ResponseTranslation } | null): ClaimPolicyOption[] {
  return _.isNil(policy) ? [] : [{ id: policy.reference, name: policy.name, isActive: true, minimumApprovals: 0, approvers: [] }];
}

/** GlobalEntityDefinition.ExpenseClaim — a fund's claim policy must apply to it. */
export const EXPENSE_CLAIM_ENTITY_CODE = 69;
